import { useContext, useEffect, useState } from "react";

import MapContext from "../map/MapContext";

import olMap from "ol/Map";
import olFeature from "ol/Feature";
import olMask from "ol-ext/filter/Mask";
import olFill from "ol/style/Fill";
import olLayer from "ol/layer/Layer";

type MaskProps = {
  feature?: olFeature;
  fill: olFill;
};

/**
 * Component to register a Mask filter on the preceeding layer.
 * @param {import("ol/feature/Feature").default} feature The OL feature to use for the mask
 * @param {import("ol/style/Fill").default} fill The OL fill to use to style the mask
 * @example
 * <Map center={center} zoom={zoom} projection={Epsg27700}>
 *   <Layers>
 *     <Tile source={osmSource} />
 *     <Mask feature={maskFeature} fill={MaskFill} />
 *   </Layers>
 * </Map>
 */
const Mask = ({ feature, fill }: MaskProps) => {
  const map = useContext(MapContext) as olMap;
  const [layer, setLayer] = useState<olLayer | null>(null);

  useEffect(() => {
    if (!map) return;
    const layer = map.getAllLayers().slice(-1)[0];
    if (!layer) return;
    setLayer(layer);
  }, [map]);

  useEffect(() => {
    if (!layer) return () => {};

    const mask = new olMask({
      feature: feature,
      wrapX: true,
      fill: fill,
    });
    layer.addFilter(mask);

    return () => {
      if (!layer) return;
      layer.removeFilter(mask);
    };
  }, [layer, feature, fill]);

  return null;
};

export default Mask;
